import React, {
  FC,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import { styled } from '@grupoboticario/flora-react';
import { useDoubleTap } from 'use-double-tap';

import { actions, CatalogContext } from 'src/contexts/Catalog';
import {
  goToNextPage,
  goToPreviousPage,
} from 'src/contexts/Catalog/store/actions';

import { MobileSlide } from './MobileSlide';

import {
  isZoomDoubleTapArea,
  MobileGoToAction,
  shouldGoTo,
} from './MobileSliderServices';
import { sendCatalogEvent } from 'src/services/googleAnalytics/helpers/catalog';
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch';
import { useStyle } from 'src/contexts/StyleContext';

export type UpdateCatalogData = (
  position: number,
  secondaryPosition?: number,
) => Promise<void>;

const StyledSlider = styled('div', {
  overflow: 'hidden',
});

const StyledSlidesWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  margin: 'auto',
  height: 'calc(100% - 48px - 62px)',
  position: 'fixed',
  top: '48px',
  left: '0px',
  overflow: 'auto',
});

type DoubleTapMode = 'zoomIn' | 'zoomOut' | 'reset' | undefined;

export const MobileSlider: FC = () => {
  const [doubleTapMode, setDoubleTapMode] = useState<DoubleTapMode>();
  const [state, dispatch] = useContext(CatalogContext);

  const { style } = useStyle();

  let touchStartXPosition: number;
  let currentZoomScale = 1;

  const slider = useRef<HTMLDivElement>(null);
  const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      touchStartXPosition = e.touches[0].clientX;

      slider?.current?.addEventListener('touchmove', handleTouchMove);
      slider?.current?.addEventListener('touchend', handleTouchEnd);
    },
    [state.page],
  );

  const resetInitialState = useCallback(() => {
    const currentSlider = slider?.current;
    currentSlider?.addEventListener('touchstart', handleTouchStart);
  }, [state.page, handleTouchStart]);

  const removeAllListeners = () => {
    slider?.current?.removeEventListener('touchmove', handleTouchMove);
    slider?.current?.removeEventListener('touchend', handleTouchEnd);
    slider?.current?.removeEventListener('touchstart', handleTouchStart);
  };

  const enableZoom = () => {
    dispatch(actions.zoomIn());
    removeAllListeners();
  };

  const disableZoom = () => {
    dispatch(actions.zoomOut());
    slider?.current?.addEventListener('touchstart', handleTouchStart);
  };

  const handlePinchStart = () => {
    enableZoom();

    const defaultZoomIn = 0.5;
    sendCatalogEvent.pinchZoomIn(defaultZoomIn);
    transformWrapperRef.current?.zoomIn(defaultZoomIn);
  };

  const handlePinchStop = (ref: ReactZoomPanPinchRef) => {
    const zoomInitialScale = 1;
    const lastZoomScale = currentZoomScale;
    currentZoomScale = ref.state.scale;

    if (currentZoomScale <= zoomInitialScale) {
      sendCatalogEvent.pinchZoomOut(zoomInitialScale);
      disableZoom();
    } else if (currentZoomScale < lastZoomScale) {
      sendCatalogEvent.pinchZoomOut(currentZoomScale);
    } else if (currentZoomScale > lastZoomScale) {
      sendCatalogEvent.pinchZoomIn(currentZoomScale);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();

    const isPinching = e.targetTouches.length > 1;

    if (isPinching) {
      handlePinchStart();
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    removeAllListeners();

    const touch = {
      endX: e.changedTouches[0].clientX,
      startX: touchStartXPosition,
    };

    const sliderInfo = {
      currentPage: state.page,
      numberOfPages: state.numberOfPages,
    };

    const sliderWidth = window.innerWidth;

    const goTo = shouldGoTo(touch, sliderInfo, sliderWidth);

    switch (goTo) {
      case MobileGoToAction.nextPageSwipe:
        sendCatalogEvent.swipeNavigationNext();
        dispatch(goToNextPage());
        break;
      case MobileGoToAction.previousPageSwipe:
        sendCatalogEvent.swipeNavigationPrev();
        dispatch(goToPreviousPage());
        break;
      case MobileGoToAction.nextPageTap:
        sendCatalogEvent.tapNavigationNext();
        dispatch(goToNextPage());
        break;
      case MobileGoToAction.previousPageTap:
        sendCatalogEvent.tapNavigationPrev();
        dispatch(goToPreviousPage());
        break;
      case MobileGoToAction.currentPage:
        resetInitialState();
        break;
    }
  };

  const handleDoubleTap = (e: React.MouseEvent) => {
    const doubleTapX = e.clientX;
    const sliderWidth = window.innerWidth;

    if (!isZoomDoubleTapArea(doubleTapX, sliderWidth)) {
      return;
    }

    if (state.isZoomActive) {
      sendCatalogEvent.doubleTapZoomOut();
      setTimeout(() => {
        disableZoom();
      }, 0);
    } else {
      sendCatalogEvent.doubleTapZoomIn();
      enableZoom();
    }
  };

  const bindTap = useDoubleTap(handleDoubleTap);

  useEffect(() => {
    resetInitialState();

    const currentSlider = slider?.current;
    return () => {
      currentSlider?.removeEventListener('touchstart', handleTouchStart);
    };
  }, [handleTouchStart, resetInitialState, state.page]);

  useEffect(() => {
    setTimeout(() => {
      setDoubleTapMode(state.isZoomActive ? 'reset' : 'zoomIn');
    }, 0);
  }, [state.isZoomActive]);

  return (
    <StyledSlider
      aria-hidden={style.isProductDrawerOpen}
      ref={slider}
      id="slider"
      data-testid="slider"
      {...bindTap}
    >
      <StyledSlidesWrapper id="slides-wrapper">
        {state.exhibition?.currentPagesData.map((page) => (
          <TransformWrapper
            ref={transformWrapperRef}
            disabled={!state.isZoomActive}
            key={page.number}
            alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
            doubleClick={{ step: 0.5, mode: doubleTapMode }}
            onZoomStop={handlePinchStop}
          >
            <TransformComponent data-testid="transform-wrapper">
              <MobileSlide page={page} hasSpinner />
            </TransformComponent>
          </TransformWrapper>
        ))}
      </StyledSlidesWrapper>
    </StyledSlider>
  );
};
